import api from "@/services/api";

/* ─── Tipos ────────────────────────────────────────────────────────────────── */

export interface ConstructionProject {
  id:   string;
  name: string;
}

export interface BudgetResource {
  resourceId:       string;
  description:      string;
  unit:             string;
  quantity:         number;
  unitPrice:        number;
  /** quantity × unitPrice (orçado total desta linha) */
  totalOrcado:      number;
  buildingUnitId:   string;
  buildingUnitName: string;
  /** URL de imagem do insumo (Sienge / API), quando existir */
  imageUrl?:        string;
}

export interface AppropriatedResource {
  resourceId:       string;
  description:      string;
  unit:             string;
  buildingUnitId:   string;
  buildingUnitName: string;
  quantity:         number;
  /** Valor realizado/apropriado */
  value:            number;
  /** Preço unitário do recurso */
  unitPrice:        number;
  /** Referência do documento (pedido de compra, NF, etc.) */
  documentLabel:    string;
  /** Data do movimento / apropriação */
  date:             string;
  /** Referência do item de orçamento */
  costEstimationRef: string;
  imageUrl?:        string;
}

export interface OrcamentoData {
  budgetResources: BudgetResource[];
  appropriated:    AppropriatedResource[];
}

/* ─── Normalização ─────────────────────────────────────────────────────────── */

function normStr(v: unknown): string {
  return v != null ? String(v).trim() : "";
}

function normNum(v: unknown): number {
  if (v == null) return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isNaN(n) ? 0 : n;
}

/** Campos comuns de URL de imagem em payloads Sienge / catálogo. */
function normImageUrl(r: Record<string, unknown>): string | undefined {
  const u = normStr(
    r.imageUrl ??
      r.image_url ??
      r.thumbnailUrl ??
      r.thumbnail_url ??
      r.photoUrl ??
      r.photo_url ??
      r.pictureUrl ??
      r.picture_url ??
      r.productImageUrl ??
      r.product_image_url ??
      r.urlImage ??
      r.url_image ??
      r.attachmentUrl ??
      r.attachment_url
  );
  return u || undefined;
}

function extractList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  const o = data as Record<string, unknown> | null;
  for (const key of ["data", "results", "content", "items"]) {
    if (Array.isArray(o?.[key])) return o![key] as Record<string, unknown>[];
  }
  return [];
}

function normalizeBudgetResource(r: Record<string, unknown>): BudgetResource {
  const resourceId  = normStr(r.id ?? r.resourceId ?? r.code ?? r.resourceCode);
  const description = normStr(
    r.description ?? r.resourceDescription ?? r.name ?? r.materialDescription
  );
  const unit      = normStr(r.unitOfMeasure ?? r.unitSymbol ?? r.unit ?? r.unitySymbol);
  const quantity  = normNum(r.quantity ?? r.budgetedQuantity ?? r.quantityBudgeted);
  const unitPrice = normNum(r.unitaryValue ?? r.unitPrice ?? r.unit_price ?? r.pricePerUnit);

  const explicitTotal = normNum(
    r.totalAmount ?? r.budgetedAmount ?? r.total ?? r.grossAmount ?? r.totalValue
  );
  const totalOrcado = explicitTotal > 0 ? explicitTotal : quantity * unitPrice;

  const buildingUnitId   = normStr(r.buildingUnitId ?? r.building_unit_id);
  const buildingUnitName = normStr(
    r.buildingUnitName ?? r.building_unit_name ?? r.buildingUnit ?? r.unitName
  );

  const imageUrl = normImageUrl(r);

  return {
    resourceId,
    description,
    unit,
    quantity,
    unitPrice,
    totalOrcado,
    buildingUnitId,
    buildingUnitName,
    ...(imageUrl ? { imageUrl } : {}),
  };
}

function normalizeAppropriatedResource(r: Record<string, unknown>): AppropriatedResource {
  const resourceId          = normStr(r.id ?? r.resourceId);
  const description         = normStr(r.description ?? r.name);
  const unit                = normStr(r.unitOfMeasure ?? r.unit);
  const buildingUnitId      = normStr(r.buildingUnitId ?? r.building_unit_id);
  const buildingUnitName    = normStr(r.buildingUnitName ?? r.building_unit_name);
  const quantity            = normNum(r.quantity);
  const value               = normNum(r.value);
  const unitPrice           = normNum(r.unitPrice ?? r.unit_price);
  const documentLabel       = normStr(r.documentLabel ?? r.document_label);
  const date                = normStr(r.movementDate ?? r.movement_date ?? r.date);
  const costEstimationRef   = normStr(
    r.costEstimationItemReference ?? r.cost_estimation_item_reference ??
    r.costEstimationItemDescription ?? r.cost_estimation_item_description
  );

  const imageUrl = normImageUrl(r);

  return {
    resourceId, description, unit,
    buildingUnitId, buildingUnitName,
    quantity, value, unitPrice,
    documentLabel, date, costEstimationRef,
    ...(imageUrl ? { imageUrl } : {}),
  };
}

/* ─── Chamadas de API ──────────────────────────────────────────────────────── */

/** Lista obras disponíveis para seleção no filtro. */
export async function fetchConstructionProjects(): Promise<ConstructionProject[]> {
  const { data } = await api.get("/api/construction-projects");
  const list = extractList(data);
  return list
    .map((p) => ({ id: normStr(p.id), name: normStr(p.name ?? `Obra ${p.id}`) }))
    .filter((p) => p.id);
}

/** Recursos do orçamento de uma obra (orçado). */
export async function fetchBudgetResources(buildingId: string): Promise<BudgetResource[]> {
  const { data } = await api.get(
    `/api/building-cost-estimations/${encodeURIComponent(buildingId)}/cost-estimate-resources`
  );
  const list = extractList(data);
  return list
    .filter((r) => r != null && typeof r === "object")
    .map(normalizeBudgetResource)
    .filter((r) => r.description || r.resourceId);
}

/** Apropriações realizadas de uma obra no período (realizado). */
export async function fetchAppropriatedResources(
  buildingId: string,
  startDate:  string,
  endDate:    string,
  options?: { includeZeroValue?: boolean }
): Promise<AppropriatedResource[]> {
  const { data } = await api.get("/api/building-resources-apropriadas", {
    params: { buildingId, startDate, endDate },
  });
  const list = extractList(data);
  const mapped = list
    .filter((r) => r != null && typeof r === "object")
    .map(normalizeAppropriatedResource);
  if (options?.includeZeroValue) {
    return mapped.filter((r) => (r.buildingUnitId && r.buildingUnitId.trim() !== "") || r.buildingUnitName.trim() !== "");
  }
  return mapped.filter((r) => r.value > 0 || r.quantity > 0);
}

/** Orçado + apropriado agregados de várias obras (mesmas chamadas do módulo Orçamento). */
export async function fetchMergedBudgetAndAppropriatedForBuildings(
  buildingIds: string[],
  startDate: string,
  endDate: string
): Promise<{ budgetResources: BudgetResource[]; appropriated: AppropriatedResource[] }> {
  const budgetResources: BudgetResource[] = [];
  const appropriated: AppropriatedResource[] = [];
  const settled = await Promise.allSettled(
    buildingIds.map(async (bid) => {
      const [b, a] = await Promise.all([
        fetchBudgetResources(bid),
        fetchAppropriatedResources(bid, startDate, endDate, { includeZeroValue: true }),
      ]);
      return { b, a };
    })
  );
  for (const r of settled) {
    if (r.status !== "fulfilled") continue;
    budgetResources.push(...r.value.b);
    appropriated.push(...r.value.a);
  }
  return { budgetResources, appropriated };
}

/* ─── Análise SC × Orçamento ─────────────────────────────────────────────── */

export type OrcamentoAnaliseVeredito = "ok" | "atencao" | "critico";

export interface OrcamentoAnaliseSolicitacaoData {
  purchaseRequestId: string;
  buildingId: string;
  buildingName: string;
  costCenter: { id: string; name: string };
  /** `quantity`: comparação em quantidade (não em R$). */
  comparisonMode?: "quantity";
  filterBuildingId?: string;
  obraAlinhada: boolean;
  period: { startDate: string; endDate: string };
  totalSolicitacao: number;
  items: Array<{
    itemNumber: string;
    description: string;
    resourceKey: string;
    quantity: number;
    unit: string;
    lineTotal: number;
    ucSplits: Array<{ ucName: string; percentage: number; allocatedValue: number }>;
    analysisRows: Array<{
      ucName: string;
      allocatedValue: number;
      orcado: number;
      realizado: number;
      simulado: number;
      pctApos: number | null;
      excede: boolean;
      semOrcamentoNestaUc: boolean;
      orcadoTotalInsumo: number;
    }>;
  }>;
  resumo: {
    linhasComExcesso: number;
    linhasSemOrcamento: number;
    veredito: OrcamentoAnaliseVeredito;
    mensagens: string[];
  };
}

export async function fetchOrcamentoAnaliseSolicitacao(params: {
  purchaseRequestId: string;
  startDate: string;
  endDate: string;
  filterBuildingId?: string;
}): Promise<OrcamentoAnaliseSolicitacaoData> {
  const body = {
    purchaseRequestId: params.purchaseRequestId.trim(),
    startDate: params.startDate,
    endDate: params.endDate,
    ...(params.filterBuildingId?.trim() ? { filterBuildingId: params.filterBuildingId.trim() } : {}),
  };
  try {
    const { data } = await api.post<OrcamentoAnaliseSolicitacaoData & { success?: boolean; error?: string }>(
      "/api/orcamento/analise-solicitacao",
      body
    );
    if ((data as { success?: boolean }).success === false) {
      const err = (data as { error?: string }).error ?? "Erro na análise.";
      throw new Error(err);
    }
    return data as OrcamentoAnaliseSolicitacaoData;
  } catch (e: unknown) {
    if (e instanceof Error && e.message && !/^Request failed with status code \d+$/i.test(e.message)) {
      throw e;
    }
    const ax = e as { response?: { data?: { error?: string }; status?: number }; message?: string };
    const fromBody = ax.response?.data?.error?.trim();
    if (fromBody) throw new Error(fromBody);
    const status = ax.response?.status;
    if (status === 405) {
      throw new Error(
        "A análise foi rejeitada (HTTP 405). Atualize a página (Ctrl+F5) e tente novamente. Se persistir, contate o TI."
      );
    }
    throw new Error(ax.message ?? "Não foi possível concluir a análise.");
  }
}
